import type { ConnectorStatus } from '@pulsepoint/shared';
import type { StatusTone } from '../../components/StatusLabel';
import type { Charger } from '../../api/chargers';

/** How each connector status reads on screen: a dot colour and a word. */
export const STATUS_DISPLAY: Record<ConnectorStatus, { tone: StatusTone; label: string; attention?: boolean }> = {
  Available: { tone: 'success', label: 'Available' },
  Preparing: { tone: 'live', label: 'Plugged in' },
  Charging: { tone: 'live', label: 'Charging' },
  SuspendedEV: { tone: 'live', label: 'Car paused' },
  SuspendedEVSE: { tone: 'neutral', label: 'Charger paused' },
  Finishing: { tone: 'neutral', label: 'Finishing' },
  Reserved: { tone: 'neutral', label: 'Reserved' },
  Unavailable: { tone: 'off', label: 'Unavailable', attention: true },
  Faulted: { tone: 'fault', label: 'Faulted', attention: true },
  Offline: { tone: 'offline', label: 'Offline', attention: true },
};

export interface ConnectorView {
  connectorId: number;
  status: ConnectorStatus;
  errorCode?: string;
  info?: string;
  at?: string;
}

/**
 * Each socket's status. A charger that isn't connected is Offline whatever it last said;
 * connector 0 (the charger as a whole) being Faulted or Unavailable applies to every socket.
 */
export function connectorsOf(c: Charger): ConnectorView[] {
  const whole = c.connectorStatus['0'];
  return Array.from({ length: c.connectorCount }, (_, i) => {
    const n = i + 1;
    if (!c.isConnected) return { connectorId: n, status: 'Offline' as const, at: c.lastSeenAt ?? undefined };
    const own = c.connectorStatus[String(n)];
    const s = whole && (whole.status === 'Faulted' || whole.status === 'Unavailable') ? whole : own;
    return s ? { connectorId: n, status: s.status, errorCode: s.errorCode, info: s.info, at: s.at } : { connectorId: n, status: 'Available' as const };
  });
}

export const needsAttention = (c: Charger) => connectorsOf(c).some((k) => STATUS_DISPLAY[k.status].attention);
export const isCharging = (c: Charger) => c.activeSessions.length > 0 || connectorsOf(c).some((k) => k.status === 'Charging');
export const chargerName = (c: Pick<Charger, 'label' | 'chargePointId'>) => c.label || c.chargePointId;

export const kw = (w: number | null | undefined) => (w == null ? '' : `${(w / 1000).toFixed(1)} kW`);
export const kwh = (wh: number) => `${(wh / 1000).toFixed(2)} kWh`;
